import {useDeleteFieldMutation} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {FullFieldFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type DeleteFieldModalUserProps = {
  field: FullFieldFragment;
  onDeleted?: () => void;
};

export const DeleteFieldModal = (
  props: DeleteFieldModalUserProps & SystemProps,
) => {
  const {field, onRequestClose, onDeleted} = props;
  const [deleteField, {loading}] = useDeleteFieldMutation();
  const [showForceOption, setShowForceOption] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async (force: boolean) => {
    setError(null);
    try {
      await deleteField({
        variables: {id: field._id, force},
      });
      onDeleted?.();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      if (err.message.includes('used in') && !force) {
        setShowForceOption(true);
      } else {
        setError(err.message);
      }
    }
  };

  const footer = showForceOption ? (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={() => handleDelete(true)}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Force delete'}
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={() => handleDelete(false)}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Delete field"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      {showForceOption ? (
        <p>
          This field is used in transactions. Force delete will remove it
          from all transactions. Are you sure?
        </p>
      ) : (
        <p>
          Are you sure you want to delete <strong>{field.name}</strong>?
          This action cannot be undone.
        </p>
      )}
    </Modal>
  );
};
